// ─────────────────────────────────────────────────────────────
// src/theme.js
// Tema global Material UI — Vó Chica (estilo iFood + brand #fdb913)
// ─────────────────────────────────────────────────────────────

import { createTheme, alpha } from "@mui/material/styles";

// Largura do menu lateral (iFood é mais “gordinho”)
export const DRAWER_WIDTH = 260; // Valor usado em todo o layout


// Paleta base de cinzas (equilíbrio similar ao iFood)
const grey = {
  25: "#FCFCFC",
  50: "#F9F9F9",
  100: "#F3F3F3",
  200: "#EAEAEA",
  300: "#DDDDDD",
  400: "#BDBDBD",
  500: "#9E9E9E",
  600: "#6F6F6F",
  700: "#3C3C3C",
  800: "#242424",
  900: "#181B23",
};

const primaryMain = "#fdb913";   // amarelo Vó Chica (oficial)
const primaryDark = "#dfa20d";
const primaryLight = "#ffcb47";

const theme = createTheme({
  palette: {
    mode: "light",
    primary: {
      main: primaryMain,
      dark: primaryDark,
      light: primaryLight,
      contrastText: grey[900],
    },
    secondary: {
      main: grey[700],
    },
    background: {
      default: grey[50],   // fundo do app
      paper: "#FFFFFF",    // cards/boxes
    },
    text: {
      primary: grey[700],
      secondary: grey[600],
      disabled: grey[400],
    },
    divider: grey[200],
    success: { main: "#2e7d32" },
    warning: { main: "#ed6c02" },
    error: { main: "#d32f2f" },
    info: { main: "#0288d1" },
    // Custom Semantic Palette (Required for Sidebar)
    semantic: {
      actionRed: "#d32f2f", // Error/Action Red
      success: "#2e7d32",
      warning: "#ed6c02",
      info: "#0288d1",
    },
  },

  shape: {
    borderRadius: 12, // cantos suaves como no iFood
  },

  typography: {
    fontFamily: "'Inter', system-ui, -apple-system, Segoe UI, Roboto, sans-serif",
    fontSize: 14,
    h1: { fontWeight: 700, fontSize: "2rem", letterSpacing: 0.2 },
    h2: { fontWeight: 700, fontSize: "1.75rem" },
    h3: { fontWeight: 600, fontSize: "1.5rem" },
    h4: { fontWeight: 600, fontSize: "1.25rem" },
    h5: { fontWeight: 600, fontSize: "1.125rem" },
    h6: { fontWeight: 700, fontSize: "1rem" },
    subtitle1: { fontWeight: 600, color: grey[700] },
    subtitle2: { fontWeight: 600, color: grey[600] },
    body1: { fontSize: "0.95rem", color: grey[700] },
    body2: { fontSize: "0.875rem", color: grey[600] },
    overline: {
      textTransform: "uppercase",
      letterSpacing: "0.06em",
      fontWeight: 700,
      color: grey[600],
    },
    button: { textTransform: "none", fontWeight: 600 },
  },

  // Sombra discreta (estilo iFood)
  shadows: [
    "none",
    "0 1px 2px rgba(0,0,0,0.04)",
    "0 2px 6px rgba(0,0,0,0.05)",
    "0 4px 12px rgba(0,0,0,0.06)",
    "0 6px 16px rgba(0,0,0,0.07)",
    ...Array(20).fill("0 8px 24px rgba(0,0,0,0.08)"),
  ],

  components: {
    // Base: remove cantos exagerados e padroniza transições
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          backgroundColor: grey[50],
        },
        "*": {
          scrollbarColor: `${grey[300]} transparent`,
          scrollbarWidth: "thin",
        },
        "*::-webkit-scrollbar": { height: 8, width: 8 },
        "*::-webkit-scrollbar-thumb": {
          backgroundColor: grey[300],
          borderRadius: 8,
        },
      },
    },

    // Drawer — largura, papel, divisória e leve “vidro” (como no print)
    MuiDrawer: {
      styleOverrides: {
        paper: ({ theme }) => ({
          width: DRAWER_WIDTH,
          borderRight: `1px solid ${theme.palette.divider}`,
          background: theme.palette.background.paper,
          backdropFilter: "blur(6px)",
          boxShadow: "4px 0 18px rgba(0,0,0,0.06)",
        }),
      },
    },

    // List / Itens — seleção discreta + borda esquerda na opção ativa
    MuiListItemButton: {
      styleOverrides: {
        root: ({ theme }) => ({
          borderRadius: 10,
          margin: "4px 8px",
          transition: "all .2s ease",
          "&:hover": {
            backgroundColor: alpha(theme.palette.primary.main, 0.06),
            transform: "translateX(3px)",
          },
          "&.Mui-selected": {
            backgroundColor: alpha(theme.palette.primary.main, 0.12),
            borderLeft: `4px solid ${theme.palette.primary.main}`,
            paddingLeft: 16,
            "& .MuiListItemIcon-root": { color: theme.palette.primary.main },
          },
        }),
      },
    },
    MuiListItemIcon: {
      styleOverrides: {
        root: { minWidth: 36, color: grey[600] },
      },
    },
    MuiListSubheader: {
      styleOverrides: {
        root: { lineHeight: 1.8, background: "transparent" },
      },
    },

    // Buttons — flat, sem sombra, com hover sutil
    MuiButton: {
      defaultProps: { disableElevation: true },
      styleOverrides: {
        root: {
          borderRadius: 24,
          textTransform: "none",
          fontWeight: 600,
          transition: "all .2s ease",
        },
        containedPrimary: {
          backgroundColor: primaryMain,
          color: grey[900],
          "&:hover": { backgroundColor: primaryLight, transform: "translateY(-1px)" },
          "&:active": { transform: "translateY(0)" },
        },
        outlinedPrimary: {
          borderColor: primaryMain,
          color: primaryMain,
          backgroundColor: "#fff",
          "&:hover": {
            borderColor: primaryDark,
            backgroundColor: alpha(primaryMain, 0.08),
          },
        },
      },
    },

    // Paper/Card — cantos e sombra suave
    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: 16,
          boxShadow: "0 4px 12px rgba(0,0,0,0.05)",
        },
      },
    },

    // Inputs — borda clara e foco amarelo
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          backgroundColor: "#fff",
          "& .MuiOutlinedInput-notchedOutline": { borderColor: grey[300] },
          "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: primaryMain },
          "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: primaryMain },
        },
      },
    },

    // Chips — usados como “etiquetas” (estilo iFood)
    MuiChip: {
      styleOverrides: {
        root: { fontWeight: 600, borderRadius: 16, height: 32 },
        filled: { backgroundColor: primaryMain, color: grey[900] },
        outlined: {
          borderColor: primaryMain,
          color: grey[700],
          "&:hover": { backgroundColor: alpha(primaryMain, 0.08) },
        },
      },
    },

    // AppBar / Toolbar — topo leve
    MuiAppBar: {
      styleOverrides: {
        root: ({ theme }) => ({
          backgroundColor: theme.palette.background.paper,
          color: theme.palette.text.primary,
          boxShadow: "0 1px 2px rgba(0,0,0,0.04)",
          borderBottom: `1px solid ${theme.palette.divider}`,
        }),
      },
    },

    // Tooltip — discreto
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          borderRadius: 8,
          padding: "8px 10px",
          fontSize: 12,
        },
      },
    },

    // Dialog — cantos e sombra
    MuiDialog: {
      styleOverrides: {
        paper: {
          borderRadius: 16,
          boxShadow: "0 10px 40px rgba(0,0,0,.12)",
        },
      },
    },

    // Divider — linha fina
    MuiDivider: {
      styleOverrides: {
        root: { borderColor: grey[200] },
      },
    },

    // IconButton/Badge — microfeedback
    MuiIconButton: {
      styleOverrides: {
        root: {
          borderRadius: 10,
          "&:hover": { backgroundColor: alpha(primaryMain, 0.08) },
        },
      },
    },
    MuiBadge: {
      styleOverrides: {
        badge: {
          fontWeight: 700,
          backgroundColor: primaryMain,
          color: grey[900],
        },
      },
    },
  },
});

export default theme;
